/**
 * 파일명 : evl0110m.tsx
 * 설명   : 마감일 관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0       2026-01-05   이예원     최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Tag, Space, Modal, DatePicker, Typography, message, Select, Tabs, Input, Switch } from "antd";
import dayjs from "dayjs";

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Evl0110mAction } from '../../../redux/slice/evl/evl0110m_slice';

/* Models */
import { IEvlMemberListState, EvaluationStatus, TabType, StatusFilter } from 'redux/models/evl/evl0110m_model';

import * as common from 'libs/common';
import * as commonProxy from 'libs/CommonProxy';

const { Text } = Typography;

const Evl0110m: React.FC = () => {
  const dispatch = useAppDispatch();

  // Local state for modal
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedRecord   , setSelectedRecord] = useState<IEvlMemberListState | null>(null);
  const [selectedDate     , setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [searchInput      , setSearchInput] = useState<string>("");  // 검색 입력 필드용 로컬 state
  
  // 필터 임시 상태 (검색 버튼 누르기 전)
  const [tempEvalType, setTempEvalType] = useState<string>("all");
  const [tempStatus, setTempStatus] = useState<StatusFilter>("all");
  const [tempTeam, setTempTeam] = useState<string>("all");

  // 관리자 여부 확인
  const isHrAdmin = common.isHrAdmin();

  // Redux state
  const selectedEvalType = useAppSelector((state)=> state.Evl0110m.selectedEvalType);
  const selectedStatus   = useAppSelector((state)=> state.Evl0110m.selectedStatus);
  const selectedTeam     = useAppSelector((state)=> state.Evl0110m.selectedTeam);
  const searchName       = useAppSelector((state)=> state.Evl0110m.searchName);
  const pageSize         = useAppSelector((state)=> state.Evl0110m.pageSize);
  const allEmployees     = useAppSelector((state)=> state.Evl0110m.allEmployees);
  const currentPage      = useAppSelector((state)=> state.Evl0110m.currentPage);
  const activeTab        = useAppSelector((state)=> state.Evl0110m.activeTab);

  // 초기 데이터 로드
  useEffect(() => {
    const empNo = common.getEmpNo();
    // 컴포넌트 마운트 시 무조건 필터 초기화 + 일반모드로 설정
    dispatch(Evl0110mAction.setActiveTab('my'));  // 항상 일반모드로 시작
    dispatch(Evl0110mAction.resetFilters());
    dispatch(Evl0110mAction.setSearchName(''));
    setSearchInput('');
    setTempEvalType('all');
    setTempStatus('all');
    setTempTeam('all');
    fetchEvaluationData(empNo, 'my', false);  // 일반모드로 데이터 조회
  }, []);  // 빈 배열로 변경하여 최초 마운트 시에만 실행

  // activeTab 변경 시 데이터 재조회
  useEffect(() => {
    const empNo = common.getEmpNo();
    fetchEvaluationData(empNo, activeTab, false);
  }, [activeTab]);

  // Redux 필터값이 변경되면 임시 상태도 동기화
  useEffect(() => {
    setTempEvalType(selectedEvalType);
    setTempStatus(selectedStatus);
    setTempTeam(selectedTeam);
  }, [selectedEvalType, selectedStatus, selectedTeam]);

  // searchName이 외부에서 변경되면 searchInput도 동기화
  useEffect(() => {
    setSearchInput(searchName);
  }, [searchName]);

  // 탭 변경 핸들러
  const handleTabChange = (key: string) => {
    dispatch(Evl0110mAction.setActiveTab(key as TabType));
    dispatch(Evl0110mAction.setSearchName(''));  // Redux의 searchName도 초기화
    setSearchInput('');  // 탭 변경 시 검색 입력 필드 초기화
  };

  // 백엔드 데이터 조회
  const fetchEvaluationData = (loginEmpNo: string, tabType: TabType, resetFilter: boolean = false) => {
    // resetFilter가 true일 때만 필터 초기화
    if (resetFilter) {
      dispatch(Evl0110mAction.resetFilters());
      setSearchInput('');  // 로컬 state도 초기화
    }

    // isHrAdmin 플래그 결정: 관리자이면서 "all" 탭일 때만 1, 나머지는 0
    const isHrAdminFlag = (isHrAdmin && tabType === "all") ? 1 : 0;

    const requestData = {
      loginEmpNo : loginEmpNo,
      evlYear    : new Date().getFullYear().toString(),
      isHrAdmin  : isHrAdminFlag,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0009',
      success: (responseData: any) => {
        const evaluationTargets = responseData.data?.EvlMemberPeriodItems ||
                                 responseData.EvlMemberPeriodItems || [];

        if (evaluationTargets.length > 0) {
          // 데이터를 그대로 변환 (그룹화 없음)
          const convertedData: IEvlMemberListState[] = evaluationTargets.map((target: any, index: number) => {
            // evlType과 evlLvl에 따라 적절한 마감일 가져오기
            let deadlineBackend = "";
            
            // ===== 변경: 각 평가별 개별 마감일 사용 (공통 마감일 아님) =====
            if (target.evlType === "BIZ") {
              // 업무서약서
              if (target.evlLvl === 0) {
                deadlineBackend = target.bizPlgEdDt || "";  // 업무서약서 마감일
              }
            } else if (target.evlType === "PJ") {
              // 프로젝트평가
              deadlineBackend = target.pjtEvlEdDt || "";  // 프로젝트평가 마감일
            } else if (target.evlType === "HR") {
              // 인사평가
              if (target.evlLvl === 1) {
                deadlineBackend = target.slfEdDt || "";  // 자기평가 마감일
              } else if (target.evlLvl === 2) {
                deadlineBackend = target.eval1EdDt || "";  // 1차평가 마감일
              } else if (target.evlLvl === 3) {
                deadlineBackend = target.eval2EdDt || "";  // 2차평가 마감일
              }
            }
            // ===== 변경 끝 =====
            const deadline = formatDateFromBackend(deadlineBackend);

            // evlType과 evlLvl에 따라 submitYn 결정
            let submitYn = "N";
            if (target.evlType === "BIZ") {
              submitYn = target.bizPlgSubYn  || "N";
            } else if (target.evlType === "PJ") {
              submitYn = target.pjtSubYn     || "N";
            } else if (target.evlType === "HR") {
              if (target.evlLvl === 1) {
                submitYn = target.slfSubYn   || "N";
              }
              else if (target.evlLvl === 2) {
                submitYn = target.eval1SubYn || "N";
              }
              else if (target.evlLvl === 3) {
                submitYn = target.eval2SubYn || "N";
              }
            }

            return {
              id               : `${target.empNo}-${target.evlType}-${target.evlLvl}-${target.projectCd || 'none'}-${target.pjtEvlCd || 'none'}-${target.eval1EmpNo || 'none'}-${target.eval2EmpNo || 'none'}-${index}`,
              empNo            : target.empNo,
              name             : target.empNm,
              team             : target.dptNm,
              position         : target.posNm,
              eval1EmpNo       : target.eval1EmpNo,
              eval1EmpNm       : target.eval1EmpNm,
              eval1PosNm       : target.eval1PosNm,
              eval2EmpNo       : target.eval2EmpNo,
              eval2EmpNm       : target.eval2EmpNm,
              eval2PosNm       : target.eval2PosNm,
              evlLvl           : target.evlLvl,
              evlType          : target.evlType,
              pmEmpNo          : target.pmEmpNo,
              pmEmpNm          : target.pmEmpNm,
              pmPosNm          : target.pmPosNm,
              pjtEvlCd         : target.pjtEvlCd,
              projectCd        : target.projectCd,
              projectNm        : target.projectNm,
              deadline         : deadline,
              status           : convertStatus(submitYn, deadlineBackend),
              isEvaluatorGroup : false,
              // 공통 마감일 정보 추가 (날짜 제한용)
              eval1CommonEdDt  : target.eval1CommonEdDt,
              eval2CommonEdDt  : target.eval2CommonEdDt,
              evlYear          : target.evlYear,
              // 제출 여부 (기한 연장 제약 체크용)
              eval1SubYn       : target.eval1SubYn,
              eval2SubYn       : target.eval2SubYn,
            };
          });

          dispatch(Evl0110mAction.setAllEmployees(convertedData));
          dispatch(Evl0110mAction.setRawBackendData(evaluationTargets));

          //message.success(`평가 대상자 ${evaluationTargets.length}명을 불러왔습니다.`);
        } else {
          message.info('평가 대상자가 없습니다.');
          dispatch(Evl0110mAction.setAllEmployees([]));
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가 대상자 조회에 실패했습니다.');
      }
    });
  };

  // 날짜 포맷 변환 (YYYYMMDD -> YYYY-MM-DD)
  const formatDateFromBackend = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) {
      return "";
    }
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  };

  // 상태 변환
  const convertStatus = (subYn: string, deadlineBackend: string): EvaluationStatus => {

    // 제출 완료 체크
    if (subYn === "Y") {
      return "completed";
    }

    // 마감일이 없으면 미작성
    if (!deadlineBackend || deadlineBackend.length !== 8) {
      return "not-started";
    }

    // 오늘 날짜와 마감일 비교 (YYYYMMDD 형식)
    const today = dayjs().format("YYYYMMDD");

    // 마감일이 오늘보다 과거이면 기한초과
    if (deadlineBackend < today) {
      return "overdue";
    }

    // 그 외에는 미작성
    return "not-started";
  };

  // 평가 유형명 반환
  const getEvaluationTypeName = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") {
      return "업무서약서";
    }
    if (employee.evlType === "PJ") {
      if (employee.pjtEvlCd === "03") {
        return "프로젝트평가-PM";
      }
      if (employee.pjtEvlCd === "04") {
        return "프로젝트평가-팀원";
      }
      return "프로젝트평가";
    }
    if (employee.evlLvl === 1) {
      return "자기평가";
    }
    if (employee.evlLvl === 2) {
      return "1차평가";
    }
    if (employee.evlLvl === 3) {
      return "2차평가";
    }
    return "자기평가";
  };

  // 평가자명 반환
  const getEvaluatorName = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") {
      return "-";
    }
    if (employee.evlType === "PJ") {
      return employee.pmEmpNm || "-";
    }
    if (employee.evlType === "HR") {
      if (employee.evlLvl === 1) {
        return "-"; // 자기평가는 평가자 없음
      }
      if (employee.evlLvl === 2) {
        return employee.eval1EmpNm || "-";
      }
      if (employee.evlLvl === 3) {
        return employee.eval2EmpNm || "-";
      }
    }
    return "-";
  };

  // 평가자직급 반환
  const getEvaluatorPosition = (employee: IEvlMemberListState): string => {
    if (employee.evlType === "BIZ") {
      return "-";
    }
    if (employee.evlType === "PJ") {
      return employee.pmPosNm || "-";
    }
    if (employee.evlType === "HR") {
      if (employee.evlLvl === 1) {
        return "-"; // 자기평가는 평가자 없음
      }
      if (employee.evlLvl === 2) {
        return employee.eval1PosNm || "-";
      }
      if (employee.evlLvl === 3) {
        return employee.eval2PosNm || "-";
      }
    }
    return "-";
  };

  // 상태 태그
  const getStatusTag = (status: string) => {
    const statusMap = {
      completed    : { color: "success"   , text: "완료" },
      "in-progress": { color: "processing", text: "진행중" },
      overdue      : { color: "error"     , text: "기한초과" },
      "not-started": { color: "default"   , text: "미작성" },
    };
    const config = statusMap[status as keyof typeof statusMap] || statusMap["not-started"];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 기한연장 핸들러
  const handleExtendDeadline = (record: IEvlMemberListState) => {
    // ===== 상위 평가 제출 여부 체크 =====
    
    // evlLvl이 0(업무서약서), 1(자기평가), 4(프로젝트평가)인 경우: 1차평가 제출여부 체크
    if (record.evlLvl === 0 || record.evlLvl === 1 || record.evlLvl === 4) {
      if (record.eval1SubYn === "Y") {
        Modal.warning({
          title: '기한 연장 불가',
          content: '1차평가가 이미 제출되어 기한을 연장할 수 없습니다.',
          okText: '확인',
        });
        return;
      }
    }
    
    // evlLvl이 2(1차평가)인 경우: 2차평가 제출여부 체크
    if (record.evlLvl === 2) {
      if (record.eval2SubYn === "Y") {
        Modal.warning({
          title: '기한 연장 불가',
          content: '2차평가가 이미 제출되어 기한을 연장할 수 없습니다.',
          okText: '확인',
        });
        return;
      }
    }
    
    // ===== 제약 조건 통과 시 모달 열기 =====
    setSelectedRecord(record);
    setSelectedDate(null); // 날짜 초기
    setIsExtendModalOpen(true);
  };

  // 상태변경 핸들러
  const handleChangeStatus = (record: IEvlMemberListState) => {
    // ===== 상위 평가 제출 여부 체크 =====
    
    // evlLvl이 0(업무서약서), 1(자기평가), 4(프로젝트평가)인 경우: 1차평가 제출여부 체크
    if (record.evlLvl === 0 || record.evlLvl === 1 || record.evlLvl === 4) {
      if (record.eval1SubYn === "Y") {
        Modal.warning({
          title: '상태 변경 불가',
          content: '1차평가가 이미 제출되어 상태를 변경할 수 없습니다.',
          okText: '확인',
        });
        return;
      }
    }
    
    // evlLvl이 2(1차평가)인 경우: 2차평가 제출여부 체크
    if (record.evlLvl === 2) {
      if (record.eval2SubYn === "Y") {
        Modal.warning({
          title: '상태 변경 불가',
          content: '2차평가가 이미 제출되어 상태를 변경할 수 없습니다.',
          okText: '확인',
        });
        return;
      }
    }
    
    // ===== 제약 조건 통과 시 확인 모달 열기 =====
    const loginEmpNo = common.getEmpNo();

    Modal.confirm({
      title      : '상태 변경',
      content    : `${record.name}님의 평가 상태를 미완료로 변경하시겠습니까?`,
      okText     : '변경',
      cancelText : '취소',
      onOk: () => {
        let txCd = '';
        if (record.evlType === "BIZ") {
          txCd = 'TREVLS0021';
        }
        else if (record.evlType === "HR") {
          txCd = 'TREVLS0019';
        }
        else if (record.evlType === "PJ") {
          txCd = 'TREVLS0020';
        }

        const requestData = {
          loginEmpNo : loginEmpNo,
          empNo      : record.empNo,
          evlYear    : new Date().getFullYear().toString(),
          evlType    : record.evlType,
          evlLvl     : record.evlLvl,
          submitYn   : "N",
          projectCd  : record.projectCd,
        };

        commonProxy.callAsyncSvc(requestData, {
          txCd    : txCd,
          success : (responseData: any) => {
            // 백엔드 응답의 status 체크
              const data = responseData.data || responseData;
              const resultCode = data.resultCode;
              
              if (resultCode === "400") {
              message.error('상위평가가 제출되어 상태 변경이 불가능합니다.')  ;
              fetchEvaluationData(loginEmpNo, activeTab); // 에러일 때 재조회
              return;
            }
            
            // SUCCESS인 경우
            message.success('평가 상태가 미완료로 변경되었습니다.');
            fetchEvaluationData(loginEmpNo, activeTab);
          },
          error   : (errorMsg: string) => {
            message.error(errorMsg || '상태 변경에 실패했습니다.');
          }
        });
      }
    });
  };

  // 필터링된 데이터
  const displayedEmployees = useMemo(() => {
    let result = allEmployees;

    // 평가 유형 필터
    if (selectedEvalType !== "all") {
      result = result.filter((emp) => {
        const typeName = getEvaluationTypeName(emp);
        return typeName === selectedEvalType;
      });
    }

    // 상태 필터
    if (selectedStatus !== "all") {
      result = result.filter((emp) => emp.status === selectedStatus);
    }

    // 팀 필터
    if (selectedTeam !== "all") {
      result = result.filter((emp) => emp.team === selectedTeam);
    }

    // 이름 필터
    if (searchName) {
      result = result.filter((emp) => emp.name.includes(searchName));
    }

    // 마감일 기준 정렬 (오늘로부터 가까운 순 - 절대값 기준) + 마감일이 같으면 이름순
    const today     = new Date();
    today.setHours(0, 0, 0, 0); // 시간 제거
    const todayTime = today.getTime();

    result = [...result].sort((a, b) => {
      const dateA = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      const dateB = b.deadline ? new Date(b.deadline).getTime() : Infinity;

      // NaN 처리 (잘못된 날짜 형식)
      const timeA = isNaN(dateA) ? Infinity : dateA;
      const timeB = isNaN(dateB) ? Infinity : dateB;

      // 오늘로부터의 절대값 차이 계산
      const diffA = Math.abs(timeA - todayTime);
      const diffB = Math.abs(timeB - todayTime);

      // 마감일 차이가 같으면 이름순 정렬 (가나다순)
      if (diffA === diffB) {
        return a.name.localeCompare(b.name, 'ko-KR');
      }

      return diffA - diffB; // 절대값 차이가 작은 순서대로 정렬
    });

    return result;
  }, [allEmployees, selectedEvalType, selectedStatus, selectedTeam, searchName]);

  // 부서 목록 (가나다순 정렬)
  const teamOptions = useMemo(() => {
    const teams       = Array.from(new Set(allEmployees.map(emp => emp.team))).filter(Boolean);
    const sortedTeams = teams.sort((a, b) => a.localeCompare(b, 'ko-KR'));
    return [
      { label: '전체', value: 'all' },
      ...sortedTeams.map(team => ({ label: team, value: team }))
    ];
  }, [allEmployees]);

  // 필터 초기화
  const handleResetFilters = () => {
    dispatch(Evl0110mAction.setSelectedEvalType('all'));
    dispatch(Evl0110mAction.setSelectedStatus('all'));
    dispatch(Evl0110mAction.setSelectedTeam('all'));
    dispatch(Evl0110mAction.setSearchName(''));
    dispatch(Evl0110mAction.resetPage());  // 페이지도 첫 페이지로
    setSearchInput('');  // 로컬 state도 초기화
    setTempEvalType('all');
    setTempStatus('all');
    setTempTeam('all');
    message.info('필터가 초기화되었습니다.');
  };

  // 검색 핸들러 (모든 필터 적용)
  const handleSearch = () => {
    dispatch(Evl0110mAction.setSearchName(searchInput));
    dispatch(Evl0110mAction.setSelectedEvalType(tempEvalType));
    dispatch(Evl0110mAction.setSelectedStatus(tempStatus));
    dispatch(Evl0110mAction.setSelectedTeam(tempTeam));
    dispatch(Evl0110mAction.resetPage());
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title     : "피평가자명",
      dataIndex : "name",
      key       : "name",
      width     : 100,
      align     : 'center' as const,
    },
    {
      title     : "피평가자직급",
      dataIndex : "position",
      key       : "position",
      width     : 90,
      align     : 'center' as const,
    },
    {
      title     : "피평가자부서",
      dataIndex : "team",
      key       : "team",
      width     : 120,
      align     : 'center' as const,
    },
    {
      title     : "평가자명",
      key       : "evaluator",
      width     : 100,
      align     : 'center' as const,
      render    : (_: any, record: IEvlMemberListState) => getEvaluatorName(record),
    },
    {
      title     : "평가자직급",
      key       : "evaluatorPosition",
      width     : 90,
      align     : 'center' as const,
      render    : (_: any, record: IEvlMemberListState) => getEvaluatorPosition(record),
    },
    {
      title     : "유형",
      key       : "type",
      width     : 250,
      align     : 'center' as const,
      render    : (_: any, record: IEvlMemberListState) => {
        const type = getEvaluationTypeName(record);
        const colorMap: { [key: string]: string } = {
          "자기평가"         : "blue",
          "1차평가"          : "green",
          "2차평가"          : "volcano",
          "업무서약서"       : "purple",
          "프로젝트평가"     : "cyan",
          "프로젝트평가-PM"  : "magenta",
          "프로젝평가-팀원": "gold",
        };
        return (
          <div>
            <Tag color={colorMap[type] || "blue"}>{type}</Tag>
            {record.evlType === "PJ" && record.projectNm && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4, textAlign: 'center' }}>
                {record.projectNm}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title     : "마감일",
      dataIndex : "deadline",
      key       : "deadline",
      width     : 110,
      align     : 'center' as const,
    },
    {
      title     : "상태",
      dataIndex : "status",
      key       : "status",
      width     : 90,
      align     : 'center' as const,
      render    : (status: string) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {getStatusTag(status)}
        </div>
      ),
    },
    {
      title     : "작업",
      key       : "action",
      width     : 180,
      align     : 'center' as const,
      render    : (_: any, record: IEvlMemberListState) => {
        // 상태변경 버튼 활성화 조건: 완료 상태이면서 오늘이 마감일 이전일 때만
        const isStatusChangeEnabled = (() => {
          if (record.status !== "completed") return false;
          if (!record.deadline) return false;
          
          const today = dayjs().startOf('day');
          const deadline = dayjs(record.deadline, 'YYYY-MM-DD').startOf('day');
          
          // 오늘이 마감일 당일이거나 이전이어야 함 (오늘 <= 마감일)
          return !today.isAfter(deadline);
        })();

        return (
          <Space size="small">
            <Button
              type     = {record.status === "overdue" ? "primary" : "default"}
              size     = "small"
              onClick  = {() => handleExtendDeadline(record)}
              danger   = {record.status === "overdue"}
            >
              기한연장
            </Button>
            <Button
              type     = "default"
              size     = "small"
              onClick  = {() => handleChangeStatus(record)}
              disabled = {!isStatusChangeEnabled}
            >
              상태변경
            </Button>
          </Space>
        );
      },
    },
  ];

  // 기한연장 시 날짜 제한 함수
  const getMaxExtendDate = (record: IEvlMemberListState | null): dayjs.Dayjs | null => {
    if (!record) return null;

    // 업무서약서: 1차평가 공통 마감일 전일까지, 없으면 9월 30일까지
    if (record.evlType === "BIZ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, 'YYYYMMDD');
        return eval1Date.subtract(1, 'day');
      } else {
        const year = record.evlYear || new Date().getFullYear().toString();
        return dayjs(`${year}-09-30`);
      }
    }
    // 자기평가: 1차평가 공통 마감일 전일까지
    else if (record.evlType === "HR" && record.evlLvl === 1) {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, 'YYYYMMDD');
        return eval1Date.subtract(1, 'day');
      }
      return null;
    }
    // 프로젝트평가: 1차평가 공통 마감일 전일까지
    else if (record.evlType === "PJ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        const eval1Date = dayjs(record.eval1CommonEdDt, 'YYYYMMDD');
        return eval1Date.subtract(1, 'day');
      }
      return null;
    }
    // 1차평가: 2차평가 공통 마감일 전일까지
    else if (record.evlType === "HR" && record.evlLvl === 2) {
      if (record.eval2CommonEdDt && record.eval2CommonEdDt.length === 8) {
        const eval2Date = dayjs(record.eval2CommonEdDt, 'YYYYMMDD');
        return eval2Date.subtract(1, 'day');
      }
      return null;
    }
    // 2차평가: 그 해 종료 14일 전까지 (12월 17일)
    else if (record.evlType === "HR" && record.evlLvl === 3) {
      const year = record.evlYear || new Date().getFullYear().toString();
      return dayjs(`${year}-12-17`);
    }

    return null;
  };

  // 기한연장 안내 메시지 생성 함수
  const getExtendLimitMessage = (record: IEvlMemberListState | null): string => {
    if (!record) return '';
    
    const maxDate = getMaxExtendDate(record);
    if (!maxDate) return '';
    
    const formattedDate = maxDate.format('YYYY-MM-DD');
    
    // 업무서약서
    if (record.evlType === "BIZ") {
      if (record.eval1CommonEdDt && record.eval1CommonEdDt.length === 8) {
        return `업무서약서는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      } else {
        return `업무서약서는 3사분기 전(${formattedDate})까지만 연장 가능합니다.`;
      }
    }
    
    // 프로젝트평가
    if (record.evlType === "PJ") {
      return `프로젝트평가는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
    }
    
    // 인사평가
    if (record.evlType === "HR") {
      // 자기평가
      if (record.evlLvl === 1) {
        return `자기평가는 1차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      }
      // 1차평가
      else if (record.evlLvl === 2) {
        return `1차평가는 2차평가 마감일 전일(${formattedDate})까지만 연장 가능합니다.`;
      }
      // 2차평가
      else if (record.evlLvl === 3) {
        return `2차평가는 당해년도 종료 14일 전(${formattedDate})까지만 연장 가능합니다.`;
      }
    }
    
    return `${formattedDate}까지 연장 가능합니다.`;
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%', padding: 24 }}>
        <div>
          <h1 style={{ textAlign: 'center', marginBottom: 24 }}>
            인사평가 마감일 관리
          </h1>
        </div>

        <div style={{ padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
          {/* 관리자만 모드 전환 Switch 표시 */}
          {isHrAdmin && (
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  borderRadius: 6,
                  backgroundColor: activeTab === 'all' ? "#e6f7ff" : "#f5f5f5",
                  border: activeTab === 'all' ? "1.5px solid #1890ff" : "1.5px solid #d9d9d9",
                  transition: "all 0.3s ease",
                }}
              >
                <Switch
                  size="small"
                  checked={activeTab === 'all'}
                  onChange={(checked) => handleTabChange(checked ? 'all' : 'my')}
                  style={{
                    backgroundColor: activeTab === 'all' ? "#1890ff" : undefined,
                  }}
                />
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: activeTab === 'all' ? "#1890ff" : "#595959",
                    width: 75,
                    textAlign: "left",
                  }}
                >
                  {activeTab === 'all' ? "관리자 모드" : "일반 모드"}
                </Text>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <Space size="middle">
              <span style={{ fontWeight: 500 }}>유형:</span>
              <Select
                style    = {{ width: 200 }}
                value    = {tempEvalType}
                onChange = {(value) => setTempEvalType(value)}
                options={[
                  { label: '전체'             , value: 'all' },
                  { label: '업무서약서'       , value: '업무서약서' },
                  { label: '자기평가'         , value: '자기평가' },
                  { label: '1차평가'          , value: '1차평가' },
                  { label: '2차평가'          , value: '2차평가' },
                  { label: '프로젝트평가-PM'  , value: '프로젝트평가-PM' },
                  { label: '프로젝트평가-팀원', value: '프로젝트평가-팀원' },
                ]}
              />
              <span style={{ fontWeight: 500 }}>상태:</span>
              <Select
                style    = {{ width: 150 }}
                value    = {tempStatus}
                onChange = {(value) => setTempStatus(value)}
                options ={[
                  { label: '전체'    , value: 'all' },
                  { label: '완료'    , value: 'completed' },
                  { label: '기한초과', value: 'overdue' },
                  { label: '미작성'  , value: 'not-started' },
                ]}
              />
              <span style={{ fontWeight: 500 }}>부서:</span>
              <Select
                style    = {{ width: 200 }}
                value    = {tempTeam}
                onChange = {(value) => setTempTeam(value)}
                options ={teamOptions}
              />
              <Input
                style    = {{ width: 200 }}
                value    = {searchInput}
                onChange = {(e) => setSearchInput(e.target.value)}
                onPressEnter = {handleSearch}  // Enter 키로도 검색 가능
                placeholder="이름 검색"
              />
              <Button type="primary" onClick={handleSearch}>검색</Button>
              <Button onClick={handleResetFilters}>초기화</Button>
            </Space>
          </div>

          <Table
            columns     = {columns}
            dataSource  = {displayedEmployees}
            rowKey      = "id"
            tableLayout = "fixed"
            pagination  = {{
              current         : currentPage,
              pageSize        : pageSize,
              showSizeChanger : true,
              pageSizeOptions : ['10', '20', '50', '100'],
              showTotal       : (total)      => `총 ${total}건`,
              onChange        : (page, size) => {
                dispatch(Evl0110mAction.setCurrentPage(page));
                if (size !== pageSize) {
                  dispatch(Evl0110mAction.setPageSize(size));
                }
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Space>

      {/* 기한연장 모달 */}
      <Modal
        title         = "기한 연장"
        width         = {400}
        open          = {isExtendModalOpen}
        okText        = "연장"
        cancelText    = "취소"
        okButtonProps = {{ disabled: !selectedDate }} // 날짜 선택 전에는 연장 버튼 비활성화
        onOk          = {() => {
          if (!selectedDate) {
            message.warning('새로운 마감일을 선택해주세요.');
            return;
          }

          const loginEmpNo = common.getEmpNo();
          const record     = selectedRecord;
          if (!record) {
            return;
          }

          let txCd = '';
          if (record.evlType      === "BIZ") {
            txCd = 'TREVLS0021';
          }
          else if (record.evlType === "HR" ) {
            txCd = 'TREVLS0019';
          }
          else if (record.evlType === "PJ" ) {
            txCd = 'TREVLS0020';
          }

          const requestData = {
            loginEmpNo : loginEmpNo,
            empNo      : record.empNo,
            evlYear    : new Date().getFullYear().toString(),
            evlType    : record.evlType,
            evlLvl     : record.evlLvl,
            evlEdDt    : selectedDate.format('YYYYMMDD'),
            projectCd  : record.projectCd,
          };

          commonProxy.callAsyncSvc(requestData, {
            txCd    : txCd,
            success : (responseData: any) => {
              const data = responseData.data || responseData;
              const resultCode = data.resultCode;
              
              if (resultCode === "400") {
                message.error('상위평가가 이미 제출 완료되어 기한을 연장할 수 없습니다.');
                fetchEvaluationData(loginEmpNo, activeTab);
                setIsExtendModalOpen(false);
                return;
              }
              
              // SUCCESS인 경우
              message.success(`기한이 ${selectedDate?.format('YYYY-MM-DD')}로 연장되었습니다.`);
              fetchEvaluationData(loginEmpNo, activeTab);
              setIsExtendModalOpen(false);
            },
            error   : (errorMsg: string) => {
              message.error(errorMsg || '기한 연장에 실패했습니다.');
            }
          });
        }}
        onCancel={() => setIsExtendModalOpen(false)}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 16 }}>
            <strong>{selectedRecord?.name}</strong>님의 평가 기한을 연장할 날짜를 선택하세요.
          </p>
          <DatePicker
            style        = {{ width: '100%' }}
            value        = {selectedDate}
            format       = "YYYY-MM-DD"
            placeholder  = "새로운 마감 선택"
            disabledDate = {(current) => {
              if (!current) return false;
              
              const today = dayjs().startOf('day');
              const maxDate = getMaxExtendDate(selectedRecord);
              
              // 오늘 이전은 비활성화
              if (current.isBefore(today)) {
                return true;
              }
              
              // 최대 날짜 제한 (평가 유형별)
              if (maxDate && current.isAfter(maxDate, 'day')) {
                return true;
              }
              
              return false;
            }}
            onChange     = {(date)    => { setSelectedDate(date); }}
          />
          {selectedRecord && getMaxExtendDate(selectedRecord) && (
            <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
              * {getExtendLimitMessage(selectedRecord)}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Evl0110m;